"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, GitMerge } from "lucide-react";
import { toast } from "sonner";
import { useUniversalAuth, useGlobalModalStack } from "@west-tennessee-consulting/ui";
import type { Contact } from "@/lib/types/database";
import { useContactHooks } from "@/lib/hooks/api/shared/graphql-hooks";
import { useModalContext } from "@/lib/hooks/ui/useModalContext";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import type { ConsolidateContactModalData } from "@/lib/modals/data/modal-data-types";

interface ContactConsolidationModalProps {
  context: any;
  id: string;
  innerProps: {
    contactId: string;
  };
}

// Helper function to convert GraphQL Contacts type to Contact type
function adaptContactsToContact(contacts: any): Contact {
  return {
    ...contacts,
    company_name: contacts.company_name ?? undefined,
    company_id: contacts.company_id ?? undefined,
    email_aliases: contacts.email_aliases?.filter((a: any): a is string => a !== null) ?? undefined,
    phone: contacts.phone ?? undefined,
    address: contacts.address ?? undefined,
    city: contacts.city ?? undefined,
    state: contacts.state ?? undefined,
    zip_code: contacts.zip_code ?? undefined,
    country: contacts.country ?? undefined,
    title: contacts.title ?? undefined,
    status: contacts.status ?? undefined,
    tags: contacts.tags?.filter((t: any): t is string => t !== null) ?? undefined,
    lifecycle_stage: contacts.lifecycle_stage ?? undefined,
    source: contacts.source ?? undefined,
    last_contacted_at: contacts.last_contacted_at ?? undefined,
    avatar_url: contacts.avatar_url ?? undefined,
    created_at: contacts.created_at ?? "",
    updated_at: contacts.updated_at ?? "",
    is_active: contacts.is_active ?? true,
  } as Contact;
}

interface ContactConsolidationFormProps {
  contactId?: string;
  onClose?: () => void;
}

/**
 * Contact Consolidation Form - Content component
 * Used by both the wrapper and legacy modal patterns
 */
export function ContactConsolidationForm({ contactId, onClose }: ContactConsolidationFormProps) {
  const { appUser, session } = useUniversalAuth();
  const [targetContactId, setTargetContactId] = useState<string>("");
  const [isConsolidating, setIsConsolidating] = useState(false);

  // Fetch contact data
  const contactHooks = useContactHooks();
  const {
    data: contactData,
    isLoading,
    refetch,
  } = contactHooks.useSingle(contactId || "", { enabled: !!contactId });
  const sourceContact = useMemo(
    () => (contactData ? adaptContactsToContact(contactData) : null),
    [contactData]
  );

  const handleConsolidate = useCallback(async () => {
    if (!targetContactId) {
      toast.error("Please select a target contact to consolidate into");
      return;
    }

    if (!sourceContact) {
      toast.error("Source contact not loaded");
      return;
    }

    if (targetContactId === sourceContact.id) {
      toast.error("Cannot consolidate a contact into itself");
      return;
    }

    setIsConsolidating(true);

    try {
      const token = session?.access_token;
      if (!token) {
        toast.error("Authentication required. Please log in again.");
        return;
      }

      const response = await fetch("/api/contacts/consolidate", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          sourceContactId: sourceContact.id,
          targetContactId: targetContactId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Failed to consolidate contacts");
      }

      toast.success(`Successfully consolidated contact: ${result.migratedCount} records migrated`);
      setTargetContactId("");
      onClose?.();
      refetch();
    } catch (error) {
      console.error("Consolidation error:", error);
      toast.error(error instanceof Error ? error.message : "Failed to consolidate contacts");
    } finally {
      setIsConsolidating(false);
    }
  }, [targetContactId, sourceContact, onClose, refetch, session?.access_token]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner />
      </div>
    );
  }

  // Show error if no contactId
  if (!contactId) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">
          No contact selected. Please click the action from a contact row.
        </p>
      </div>
    );
  }

  // Show error if no contact data
  if (!sourceContact) {
    return (
      <div className="p-4 text-center">
        <p className="text-sm text-gray-500">Unable to load contact data for ID: {contactId}</p>
        <p className="text-xs text-gray-400 mt-2">
          Please try again or contact support if the problem persists.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription>
          This action will merge all data from the source contact into the target contact and then
          delete the source contact. This cannot be undone.
        </AlertDescription>
      </Alert>

      {/* Source Contact Display */}
      <div className="p-4 bg-card-50 rounded-lg border border-gray-200 dark:border-gray-700">
        <p className="text-sm font-medium text-gray-700 mb-2 dark:text-gray-300">Source Contact (will be deleted):</p>
        <p className="text-lg font-semibold text-card-foreground">{sourceContact.name}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400">{sourceContact.email}</p>
      </div>

      {/* Target Contact Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Target Contact (keep this one):</label>
        <ClientSelectWithModal
          value={targetContactId}
          onValueChange={setTargetContactId}
          placeholder="Select target contact..."
          highZIndex={true}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onClose} disabled={isConsolidating}>
          Cancel
        </Button>
        <Button
          onClick={handleConsolidate}
          disabled={isConsolidating || !targetContactId}
          className="bg-red-600 hover:bg-red-700"
        >
          {isConsolidating ? (
            <>
              <GitMerge className="w-4 h-4 mr-2 animate-spin" />
              Consolidating...
            </>
          ) : (
            <>
              <GitMerge className="w-4 h-4 mr-2" />
              Consolidate
            </>
          )}
        </Button>
      </div>
    </div>
  );
}

/**
 * Contact Consolidation Modal - Mantine modals pattern (legacy)
 * Receives contactId via innerProps
 */
export function ContactConsolidationModal({
  context,
  id,
  innerProps,
}: ContactConsolidationModalProps) {
  return (
    <ContactConsolidationForm
      contactId={innerProps?.contactId}
      onClose={() => context.closeModal(id)}
    />
  );
}

/**
 * Contact Consolidation Modal Wrapper - Global Modal Stack pattern
 * Reads contactId from modal context via useModalContext hook
 */
export function ContactConsolidationModalWrapper() {
  const { closeModal } = useGlobalModalStack();
  const modalData = useModalContext<ConsolidateContactModalData>("consolidate-contact");
  const contactId = modalData?.contactId;

  return (
    <ContactConsolidationForm
      contactId={contactId}
      onClose={() => closeModal("consolidate-contact")}
    />
  );
}
